import { instanceQueryService } from '@/services'

export type Option = {
  name: string;
  value: string;
};

export type ColSpec = {
  colName: string;
  colType: string;
};

export type FileList = {
  fileName: string;
  sizeBytes: number;
  creationTime: string;
  modifyTime: string;
};

export type FilePreview = {
  rowNumber: number;
  columnNumber: number;
  value: string;
  error: string;
};

export class ExternalService {
  private formatOptions(options: Option[], parameters: Record<string, any>): string {
    return options
      .map((o, i) => {
        const paramKey = `opt_${o.name.replace(/[^a-zA-Z0-9_]/g, '_')}_${i}`
        parameters[paramKey] = String(o.value)
        return `${o.name} $(${paramKey})`
      })
      .join(', ')
  }

  private formatColSpecs(colSpecs: ColSpec[], parameters: Record<string, any>): string {
    const base = Object.keys(parameters).length + 1
    return colSpecs.map((c, i) => {
      const nameKey = `col_name_${base + i}`
      const typeKey = `col_type_${base + i}`
      parameters[nameKey] = c.colName
      parameters[typeKey] = c.colType
      return `$(${nameKey}) $(${typeKey})`
    }).join(', ')
  }

  private formatStringList(list: string[], parameters: Record<string, any>): string {
    const base = Object.keys(parameters).length + 1
    return list.map((s, i) => {
      const key = `str_${base + i}`
      parameters[key] = s
      return `$(${key})`
    }).join(', ')
  }

  private withOptions(statement: string, options: Record<string, string>): string {
    let sql = ''
    Object.entries(options).forEach(([key, value]) => {
      if (key.match(/^GUC:/)) {
        key = key.substring(4)
        sql += `SET ${key} TO '${value}';\n`
      }
    })
    return sql + statement
  }

  /**
   * Creates an external storage.
   *
   * @param database - database is name of database
   * @param options - OPTIONAL options are a map of key / value entries, example for controlling GUC settings on the back-end:
   *        ex. set tags and hints {"GUC:ybd_query_tags": "testtag", "GUC:ybd_hint": "injectLatencySubmit=5000"}
   * @param orReplace - OPTIONAL orReplace is a boolean, if true it adds OR REPLACE to SQL command, if false it omits it from SQL command
   * @param ifNotExists - OPTIONAL ifNotExists is a boolean, if true it adds IF NOT EXISTS to SQL command, if false it omits it from SQL command
   * @param name - name is the name for this external storage.
   * @param storageType - storageType is the type of storage for this external storage, ex. 'S3'
   * @param endpoint - endpoint is the URL to where the external storage exists, ex. 's3://bucket_name/'
   * @param region - region is where the external storage resides, ex. 'us-west-1'
   * @param identity - identity is the identity to use to access this external storage, ex. 'AKIAJA5MV57W7NXMQOEA'
   * @param credential - credential is the credentials to use to access this external storage, ex. 'FZuRivow2frcwzAEg8/RqDpZb1pNLcBR2Zfnd4cp'
   * @param commandOptions - OPTIONAL commandOptions are a list of name / value pairs and are used if additional configuration is
   *        required to access this external storage, ex. exclude certain files from user purview ["--exclude": "PROD"]
   * @returns {Promise<*>}
   */
  async createExternalStorage(
    instanceId: string,
    database: string,
    options: Record<string, string>,
    orReplace: boolean,
    ifNotExists: boolean,
    name: string,
    storageType: string,
    endpoint: string,
    region: string,
    identity: string,
    credential: string,
    commandOptions: Option[]
  ): Promise<any> {
    const parameters: Record<string, any> = { name }
    let sql = 'CREATE'
    if (orReplace) { sql += ' OR REPLACE' }
    sql += ' EXTERNAL STORAGE'
    if (ifNotExists) { sql += ' IF NOT EXISTS' }
    sql += ` $(name:name) TYPE ${storageType}`
    if (endpoint) {
      sql += ' ENDPOINT $(endpoint)'
      parameters.endpoint = endpoint
    }
    if (region) {
      sql += ' REGION $(region)'
      parameters.region = region
    }
    if (identity) {
      sql += ' IDENTITY $(identity)'
      parameters.identity = identity
    }
    if (credential) {
      sql += ' CREDENTIAL $(credential)'
      parameters.credential = credential
    }
    if (commandOptions?.length) {
      sql += ` WITH ADVANCED (${this.formatOptions(commandOptions, parameters)})`
    }
    if (options?.preflight) {
      return { results: { sql: await instanceQueryService.formatQuery(sql, parameters) } }
    }
    return await instanceQueryService.sql(instanceId, database, this.withOptions(sql, options), { parameters })
  }

  /**
   * Creates an external format.
   *
   * @param database - database is name of database
   * @param options - OPTIONAL options are a map of key / value entries, example for controlling GUC settings on the back-end:
   *        ex. set tags and hints {"GUC:ybd_query_tags": "testtag", "GUC:ybd_hint": "injectLatencySubmit=5000"}
   * @param orReplace - OPTIONAL orReplace is a boolean, if true it adds OR REPLACE to SQL command, if false it omits it from SQL command
   * @param ifNotExists - OPTIONAL ifNotExists is a boolean, if true it adds IF NOT EXISTS to SQL command, if false it omits it from SQL command
   * @param name - name is the name for this external format.
   * @param fileType - fileType is the type of format, ex. 'csv'
   * @param commandOptions - OPTIONAL commandOptions are a list of name / value pairs and are used for additional configuration,
   *        ex. [{"nullmarker": "<NULL>"}, {"emptymarker": "<EMPTY>"}]
   * @returns {Promise<*>}
   */
  async createExternalFormat(
    instanceId: string,
    database: string,
    options: Record<string, string>,
    orReplace: boolean,
    ifNotExists: boolean,
    name: string,
    fileType: string,
    commandOptions: Option[]
  ): Promise<any> {
    const parameters: Record<string, any> = { name }
    let sql = 'CREATE'
    if (orReplace) { sql += ' OR REPLACE' }
    sql += ' EXTERNAL FORMAT'
    if (ifNotExists) { sql += ' IF NOT EXISTS' }
    sql += ` $(name:name) TYPE ${fileType}`
    if (commandOptions?.length) {
      sql += ` WITH (${this.formatOptions(commandOptions, parameters)})`
    }
    if (options?.preflight) {
      return { results: { sql: await instanceQueryService.formatQuery(sql, parameters) } }
    }
    return await instanceQueryService.sql(instanceId, database, this.withOptions(sql, options), { parameters })
  }

  /**
   * Creates and external location.
   *
   * @param database - database is name of database
   * @param options - OPTIONAL options are a map of key / value entries, example for controlling GUC settings on the back-end:
   *        ex. set tags and hints {"GUC:ybd_query_tags": "testtag", "GUC:ybd_hint": "injectLatencySubmit=5000"}
   * @param orReplace - OPTIONAL orReplace is a boolean, if true it adds OR REPLACE to SQL command, if false it omits it from SQL command
   * @param ifNotExists - OPTIONAL ifNotExists is a boolean, if true it adds IF NOT EXISTS to SQL command, if false it omits it from SQL command
   * @param name - name is the name for this external location.
   * @param locationPath - locationPath is the object store name/path, ex. S3 bucket name 'ybcs-unit-test-data'
   * @param storageName - storageName is the external storage name associated to this location
   * @param formatName - formatName is the external format name associated to this location
   * @param allowedPrefixes - OPTIONAL list of allowed file prefixes, ex. ['DEV', 'TEST']
   * @param disallowedPrefixes - OPTIONAL list of disallowed file prefixes, ex. ['PROD', 'GA']
   * @param readOnly - OPTIONAL readOnly is a boolean, if true it sets location to READ_ONLY, if false it omits
   * @param writeOnly - OPTIONAL writeOnly is a boolean, if true it sets location to WRITE_ONLY, if false it omits
   * @returns {Promise<*>}
   */
  async createExternalLocation(
    instanceId: string,
    database: string,
    options: Record<string, string>,
    orReplace: boolean,
    ifNotExists: boolean,
    name: string,
    location: string,
    storageName: string,
    formatName: string,
    allowedPrefix: string[],
    disallowedPrefix: string[],
    readOnly: boolean,
    writeOnly: boolean
  ): Promise<any> {
    const parameters: Record<string, any> = { name, location, storageName }
    let sql = 'CREATE'
    if (orReplace) { sql += ' OR REPLACE' }
    sql += ' EXTERNAL LOCATION'
    if (ifNotExists) { sql += ' IF NOT EXISTS' }
    sql += ' $(name:name) PATH $(location) EXTERNAL STORAGE $(storageName:name)'
    if (formatName) {
      sql += ' EXTERNAL FORMAT $(formatName:name)'
      parameters.formatName = formatName
    }
    if (allowedPrefix?.length) {
      sql += ` ALLOWED PREFIX (${this.formatStringList(allowedPrefix, parameters)})`
    }
    if (disallowedPrefix?.length) {
      sql += ` DISALLOWED PREFIX (${this.formatStringList(disallowedPrefix, parameters)})`
    }
    if (readOnly) { sql += ' READ ONLY' }
    if (writeOnly) { sql += ' WRITE ONLY' }
    if (options?.preflight) {
      return { results: { sql: await instanceQueryService.formatQuery(sql, parameters) } }
    }
    return await instanceQueryService.sql(instanceId, database, this.withOptions(sql, options), { parameters })
  }

  /**
   * Creates an external table that maps to one or more files from the associated external table.
   *
   * @param database - database is name of database
   * @param options - OPTIONAL options are a map of key / value entries, example for controlling GUC settings on the back-end:
   *        ex. set tags and hints {"GUC:ybd_query_tags": "testtag", "GUC:ybd_hint": "injectLatencySubmit=5000"}
   * @param orReplace - OPTIONAL orReplace is a boolean, if true it adds OR REPLACE to SQL command, if false it omits it from SQL command
   * @param ifNotExists - OPTIONAL ifNotExists is a boolean, if true it adds IF NOT EXISTS to SQL command, if false it omits it from SQL command
   * @param name - name is the name for this external table.
   * @param columnSpecs - OPTIONAL columnSpecs is a list of key / value pairs listing each column name and column type,
   *        ex. [{"column1": "varchar(255)"}, {"column2": "varchar(255)"}, {"column3": "varchar(255)"}]
   * @param fileGlobs - fileGlobs is a list of file names or file name prefixes to assocate to the external table,
   *        ex. ['my-file.csv', 'test']
   *        would associate file 'my-file.csv' and any file starting with 'test'
   * @param locationName - locationName is the name of the location associated to this external table
   * @param formatName - formatName is the external format name associated to this table
   * @param formatType - OPTIONAL formatType is not used if  formatName is populated, otherwise it is required, ex. 'csv'
   * @param formatTypeOptions - OPTIONAL formatTypeOptions is not used if formatName is populated, otherwise it is a list of one or more format options,
   *        ex. [{"nullmarker": "<NULL>"}, {"emptymarker": "<EMPTY>"}]
   * @param commandOptions - OPTIONAL commandOptions are a list of name / value pairs and are used if additional configuration is
   *        required to access this external storage, ex. exclude certain files from user purview ["--exclude": "PROD"]
   * @returns {Promise<*>}
   */
  async createExternalTable(
    instanceId: string,
    database: string,
    options: Record<string, string>,
    orReplace: boolean,
    ifNotExists: boolean,
    name: string,
    columnSpecs: ColSpec[],
    fileGlobs: string[],
    locationName: string,
    formatName: string,
    formatType: string,
    formatTypeOptions: Option[],
    commandOptions: Option[]
  ): Promise<any> {
    const parameters: Record<string, any> = { name, locationName }
    let sql = 'CREATE'
    if (orReplace) { sql += ' OR REPLACE' }
    sql += ' EXTERNAL TABLE'
    if (ifNotExists) { sql += ' IF NOT EXISTS' }
    sql += ` $(name:name) (${this.formatColSpecs(columnSpecs, parameters)})`
    if (fileGlobs?.length) {
      sql += ` FROM (${this.formatStringList(fileGlobs, parameters)})`
    }
    sql += ' EXTERNAL LOCATION $(locationName)'
    if (formatName) {
      sql += ' EXTERNAL FORMAT $(formatName)'
      parameters.formatName = formatName
    } else {
      sql += ` EXTERNAL FORMAT (TYPE ${formatType}`
      if (formatTypeOptions?.length) {
        sql += ` WITH (${this.formatOptions(formatTypeOptions, parameters)})`
      }
      sql += ')'
    }
    if (commandOptions?.length) {
      sql += ` WITH (${this.formatOptions(commandOptions, parameters)})`
    }
    if (options?.preflight) {
      return { results: { sql: await instanceQueryService.formatQuery(sql, parameters) } }
    }
    return await instanceQueryService.sql(instanceId, database, this.withOptions(sql, options), { parameters })
  }

  /**
   * Drops an external storage.
   *
   * @param database - database associated to this external storage.
   * @param options - OPTIONAL options are a map of key / value entries, example for controlling GUC settings on the back-end:
   *        ex. set tags and hints {"GUC:ybd_query_tags": "testtag", "GUC:ybd_hint": "injectLatencySubmit=5000"}
   * @param ifExists - ifExists is a boolean, if true it will only execute DROP if the storage exists, otherwise it executes
   *        command without verifying it exists
   * @param name - name of external storage
   * @param cascade - cascade is a boolean, if true it drops all associated external formats, locations, and tables, if false
   *        it will only attempt to drop only the external storage but if there are any associated formats, locations or
   *        tables it will throw an exception
   * @returns {Promise<*>}
   */
  async dropExternalStorage(
    instanceId: string,
    database: string,
    options: Record<string, string>,
    ifExists: boolean,
    name: string,
    cascade: boolean
  ): Promise<any> {
    const parameters: Record<string, any> = { name }
    let sql = 'DROP EXTERNAL STORAGE'
    if (ifExists) { sql += ' IF EXISTS' }
    sql += ' $(name:name)'
    if (cascade) { sql += ' CASCADE' }
    if (options?.preflight) {
      return { results: { sql: await instanceQueryService.formatQuery(sql, parameters) } }
    }
    return await instanceQueryService.sql(instanceId, database, this.withOptions(sql, options), { parameters })
  }

  /**
   * Drops an external format.
   *
   * @param database - database associated to this external format.
   * @param options - OPTIONAL options are a map of key / value entries, example for controlling GUC settings on the back-end:
   *        ex. set tags and hints {"GUC:ybd_query_tags": "testtag", "GUC:ybd_hint": "injectLatencySubmit=5000"}
   * @param ifExists - ifExists is a boolean, if true it will only execute DROP if the format exists, otherwise it executes
   *        command without verifying it exists
   * @param name - name of external format
   * @param cascade - cascade is a boolean, if true it drops all associated external locations, and tables, if false
   *        it will only attempt to drop only the external format but if there are any associated locations or
   *        tables it will throw an exception
   * @returns {Promise<*>}
   */
  async dropExternalFormat(
    instanceId: string,
    database: string,
    options: Record<string, string>,
    ifExists: boolean,
    name: string,
    cascade: boolean
  ): Promise<any> {
    const parameters: Record<string, any> = { name }
    let sql = 'DROP EXTERNAL FORMAT'
    if (ifExists) { sql += ' IF EXISTS' }
    sql += ' $(name:name)'
    if (cascade) { sql += ' CASCADE' }
    if (options?.preflight) {
      return { results: { sql: await instanceQueryService.formatQuery(sql, parameters) } }
    }
    return await instanceQueryService.sql(instanceId, database, this.withOptions(sql, options), { parameters })
  }

  /**
   * Drops an external location.
   *
   * @param database - database associated to this external location.
   * @param options - OPTIONAL options are a map of key / value entries, example for controlling GUC settings on the back-end:
   *        ex. set tags and hints {"GUC:ybd_query_tags": "testtag", "GUC:ybd_hint": "injectLatencySubmit=5000"}
   * @param ifExists - ifExists is a boolean, if true it will only execute DROP if the location exists, otherwise it executes
   *        command without verifying it exists
   * @param name - name of external location
   * @param cascade - cascade is a boolean, if true it drops all associated external tables, if false it will only attempt
   *        to drop only the external location but if there are any associated tables it will throw an exception
   * @returns {Promise<*>}
   */
  async dropExternalLocation(
    instanceId: string,
    database: string,
    options: Record<string, string>,
    ifExists: boolean,
    name: string,
    cascade: boolean
  ): Promise<any> {
    const parameters: Record<string, any> = { name }
    let sql = 'DROP EXTERNAL LOCATION'
    if (ifExists) { sql += ' IF EXISTS' }
    sql += ' $(name:name)'
    if (cascade) { sql += ' CASCADE' }
    if (options?.preflight) {
      return { results: { sql: await instanceQueryService.formatQuery(sql, parameters) } }
    }
    return await instanceQueryService.sql(instanceId, database, this.withOptions(sql, options), { parameters })
  }

  /**
   * Drops an external table.
   *
   * @param database - database associated to this external table.
   * @param options - OPTIONAL options are a map of key / value entries, example for controlling GUC settings on the back-end:
   *        ex. set tags and hints {"GUC:ybd_query_tags": "testtag", "GUC:ybd_hint": "injectLatencySubmit=5000"}
   * @param ifExists - ifExists is a boolean, if true it will only execute DROP if the table exists, otherwise it executes
   *        command without verifying it exists
   * @param name - name of external table
   * @returns {Promise<*>}
   */
  async dropExternalTable(
    instanceId: string,
    database: string,
    options: Record<string, string>,
    ifExists: boolean,
    name: string
  ): Promise<any> {
    const parameters: Record<string, any> = { name }
    let sql = 'DROP EXTERNAL TABLE'
    if (ifExists) { sql += ' IF EXISTS' }
    sql += ' $(name:name)'
    if (options?.preflight) {
      return { results: { sql: await instanceQueryService.formatQuery(sql, parameters) } }
    }
    return await instanceQueryService.sql(instanceId, database, this.withOptions(sql, options), { parameters })
  }

  /**
   * Alters an external storage.
   *
   * @param database - database is name of database
   * @param options - OPTIONAL options are a map of key / value entries, example for controlling GUC settings on the back-end:
   *        ex. set tags and hints {"GUC:ybd_query_tags": "testtag", "GUC:ybd_hint": "injectLatencySubmit=5000"}
   * @param ifNotExists - OPTIONAL ifNotExists is a boolean, if true it adds IF NOT EXISTS to SQL command, if false it omits it from SQL command
   * @param name - name is the name for this external storage.
   * @param storageType - storageType is the type of storage for this external storage, ex. 'S3'
   * @param endpoint - endpoint is the URL to where the external storage exists, ex. 's3://bucket_name/'
   * @param region - region is where the external storage resides, ex. 'us-west-1'
   * @param identity - identity is the identity to use to access this external storage, ex. 'AKIAJA5MV57W7NXMQOEA'
   * @param credential - credential is the credentials to use to access this external storage, ex. 'FZuRivow2frcwzAEg8/RqDpZb1pNLcBR2Zfnd4cp'
   * @param commandOptions - OPTIONAL commandOptions are a list of name / value pairs and are used if additional configuration is
   *        required to access this external storage, ex. exclude certain files from user purview ["--exclude": "PROD"]
   * @returns {Promise<*>}
   */
  async alterExternalStorage(
    instanceId: string,
    database: string,
    options: Record<string, string>,
    ifNotExists: boolean,
    name: string,
    storageType: string,
    endpoint: string,
    region: string,
    identity: string,
    credential: string,
    commandOptions: Option[]
  ): Promise<any> {
    const parameters: Record<string, any> = { name }
    let sql = 'ALTER EXTERNAL STORAGE'
    if (ifNotExists) { sql += ' IF NOT EXISTS' }
    sql += ` $(name:name) TYPE ${storageType}`
    if (endpoint) {
      sql += ' ENDPOINT $(endpoint)'
      parameters.endpoint = endpoint
    }
    if (region) {
      sql += ' REGION $(region)'
      parameters.region = region
    }
    if (identity) {
      sql += ' IDENTITY $(identity)'
      parameters.identity = identity
    }
    if (credential) {
      sql += ' CREDENTIAL $(credential)'
      parameters.credential = credential
    }
    if (commandOptions?.length) {
      sql += ` WITH ADVANCED (${this.formatOptions(commandOptions, parameters)})`
    }
    if (options?.preflight) {
      return { results: { sql: await instanceQueryService.formatQuery(sql, parameters) } }
    }
    return await instanceQueryService.sql(instanceId, database, this.withOptions(sql, options), { parameters })
  }

  /**
   * Alters and external location.
   *
   * @param database - database is name of database
   * @param options - OPTIONAL options are a map of key / value entries, example for controlling GUC settings on the back-end:
   *        ex. set tags and hints {"GUC:ybd_query_tags": "testtag", "GUC:ybd_hint": "injectLatencySubmit=5000"}
   * @param ifNotExists - OPTIONAL ifNotExists is a boolean, if true it adds IF NOT EXISTS to SQL command, if false it omits it from SQL command
   * @param name - name is the name for this external location.
   * @param locationPath - locationPath is the object store name/path, ex. S3 bucket name 'ybcs-unit-test-data'
   * @param storageName - storageName is the external storage name associated to this location
   * @param formatName - formatName is the external format name associated to this location
   * @param allowedPrefixes - OPTIONAL list of allowed file prefixes, ex. ['DEV', 'TEST']
   * @param disallowedPrefixes - OPTIONAL list of disallowed file prefixes, ex. ['PROD', 'GA']
   * @param readOnly - OPTIONAL readOnly is a boolean, if true it sets location to READ_ONLY, if false it omits
   * @param writeOnly - OPTIONAL writeOnly is a boolean, if true it sets location to WRITE_ONLY, if false it omits
   * @returns {Promise<*>}
   */
  async alterExternalLocation(
    instanceId: string,
    database: string,
    options: Record<string, string>,
    ifNotExists: boolean,
    name: string,
    location: string,
    storageName: string,
    formatName: string,
    allowedPrefix: string[],
    disallowedPrefix: string[],
    readOnly: boolean,
    writeOnly: boolean
  ): Promise<any> {
    const parameters: Record<string, any> = { name }
    let sql = 'ALTER EXTERNAL LOCATION'
    if (ifNotExists) { sql += ' IF NOT EXISTS' }
    sql += ' $(name:name)'
    if (location) {
      sql += ' PATH $(location)'
      parameters.location = location
    }
    if (storageName) {
      sql += ' EXTERNAL STORAGE $(storageName:name)'
      parameters.storageName = storageName
    }
    if (formatName) {
      sql += ' EXTERNAL FORMAT $(formatName:name)'
      parameters.formatName = formatName
    }
    // readOnly and writeOnly were disabled in the Java grammar
    if (options?.preflight) {
      return { results: { sql: await instanceQueryService.formatQuery(sql, parameters) } }
    }
    return await instanceQueryService.sql(instanceId, database, this.withOptions(sql, options), { parameters })
  }

  /**
   * Alters an external format.
   *
   * @param database - database is name of database
   * @param options - OPTIONAL options are a map of key / value entries, example for controlling GUC settings on the back-end:
   *        ex. set tags and hints {"GUC:ybd_query_tags": "testtag", "GUC:ybd_hint": "injectLatencySubmit=5000"}
   * @param ifNotExists - OPTIONAL ifNotExists is a boolean, if true it adds IF NOT EXISTS to SQL command, if false it omits it from SQL command
   * @param name - name is the name for this external format.
   * @param fileType - fileType is the type of format, ex. 'csv'
   * @param commandOptions - OPTIONAL commandOptions are a list of name / value pairs and are used for additional configuration,
   *        ex. [{"nullmarker": "<NULL>"}, {"emptymarker": "<EMPTY>"}]
   * @returns {Promise<*>}
   */
  async alterExternalFormat(
    instanceId: string,
    database: string,
    options: Record<string, string>,
    ifNotExists: boolean,
    name: string,
    formatType: string,
    commandOptions: Option[]
  ): Promise<any> {
    const parameters: Record<string, any> = { name }
    let sql = 'ALTER EXTERNAL FORMAT'
    if (ifNotExists) { sql += ' IF NOT EXISTS' }
    sql += ` $(name:name) TYPE ${formatType}`
    if (commandOptions?.length) {
      sql += ` WITH (${this.formatOptions(commandOptions, parameters)})`
    }
    if (options?.preflight) {
      return { results: { sql: await instanceQueryService.formatQuery(sql, parameters) } }
    }
    return await instanceQueryService.sql(instanceId, database, this.withOptions(sql, options), { parameters })
  }

  /**
   * Alters an external table that maps to one or more files from the associated external table.
   *
   * @param database - database is name of database
   * @param options - OPTIONAL options are a map of key / value entries, example for controlling GUC settings on the back-end:
   *        ex. set tags and hints {"GUC:ybd_query_tags": "testtag", "GUC:ybd_hint": "injectLatencySubmit=5000"}
   * @param ifNotExists - OPTIONAL ifNotExists is a boolean, if true it adds IF NOT EXISTS to SQL command, if false it omits it from SQL command
   * @param name - name is the name for this external table.
   * @param columnSpecs - OPTIONAL columnSpecs is a list of key / value pairs listing each column name and column type,
   *        ex. [{"column1": "varchar(255)"}, {"column2": "varchar(255)"}, {"column3": "varchar(255)"}]
   * @param fileGlobs - fileGlobs is a list of file names or file name prefixes to assocate to the external table,
   *        ex. ['my-file.csv', 'test']
   *        would associate file 'my-file.csv' and any file starting with 'test'
   * @param locationName - locationName is the name of the location associated to this external table
   * @param formatName - formatName is the external format name associated to this table
   * @param formatType - OPTIONAL formatType is not used if  formatName is populated, otherwise it is required, ex. 'csv'
   * @param formatTypeOptions - OPTIONAL formatTypeOptions is not used if formatName is populated, otherwise it is a list of one or more format options,
   *        ex. [{"nullmarker": "<NULL>"}, {"emptymarker": "<EMPTY>"}]
   * @param commandOptions - OPTIONAL commandOptions are a list of name / value pairs and are used if additional configuration is
   *        required to access this external storage, ex. exclude certain files from user purview ["--exclude": "PROD"]
   * @returns {Promise<*>}
   */
  async alterExternalTable(
    instanceId: string,
    database: string,
    options: Record<string, string>,
    ifNotExists: boolean,
    name: string,
    columnSpecs: ColSpec[],
    fileGlobs: string[],
    locationName: string,
    formatName: string,
    formatType: string,
    formatTypeOptions: Option[]
  ): Promise<any> {
    const parameters: Record<string, any> = { name, locationName }
    let sql = 'ALTER EXTERNAL TABLE'
    if (ifNotExists) { sql += ' IF NOT EXISTS' }
    sql += ' $(name:name)'
    if (columnSpecs?.length) {
      sql += ` (${this.formatColSpecs(columnSpecs, parameters)})`
    }
    if (fileGlobs?.length) {
      sql += ` FROM (${this.formatStringList(fileGlobs, parameters)})`
    }
    sql += ' ON $(locationName)'
    if (formatName) {
      sql += ' EXTERNAL FORMAT $(formatName:name)'
      parameters.formatName = formatName
    } else {
      sql += ` EXTERNAL FORMAT (TYPE ${formatType}`
      if (formatTypeOptions?.length) {
        sql += ` WITH (${this.formatOptions(formatTypeOptions, parameters)})`
      }
      sql += ')'
    }
    if (options?.preflight) {
      return { results: { sql: await instanceQueryService.formatQuery(sql, parameters) } }
    }
    return await instanceQueryService.sql(instanceId, database, this.withOptions(sql, options), { parameters })
  }

  async list(
    instanceId: string,
    database: string,
    options: Record<string, string>,
    locationName: string,
    prefix: string,
    suffix: string,
    limit: number,
    startWithKey: string
  ) {
    const parameters: Record<string, any> = { locationName }
    let sql = 'LIST OBJECTS EXTERNAL LOCATION $(locationName:name)'
    if (prefix) {
      sql += ' PREFIX ($(prefix))'
      parameters.prefix = prefix
    }
    if (suffix) {
      sql += ' SUFFIX ($(suffix))'
      parameters.suffix = suffix
    }
    if (limit) {
      sql += ' LIMIT $(limit)'
      parameters.limit = limit
    }
    if (startWithKey) {
      sql += ' START AFTER $(startWithKey)'
      parameters.startWithKey = startWithKey
    }
    if (options?.preflight) {
      return { results: { sql: await instanceQueryService.formatQuery(sql, parameters) } }
    }
    const result = await instanceQueryService.sql(instanceId, database, this.withOptions(sql, options), { parameters, asyncTimeout: 30 * 1000 })
    return result.rows
  }

  async preview(
    instanceId: string,
    database: string,
    options: Record<string, string>,
    locationName: string,
    columnSpecs: ColSpec[],
    fileGlobs: string[],
    formatName: string,
    formatType: string,
    commandOptions: Option[],
    rowLimit: number,
    skipRows: number
  ): Promise<any> {
    const parameters: Record<string, any> = { rowLimit, skipRows }
    let sql = 'PREVIEW'
    if (columnSpecs?.length) {
      sql += ` (${this.formatColSpecs(columnSpecs, parameters)})`
    }
    if (fileGlobs?.length) {
      sql += ` FROM (${this.formatStringList(fileGlobs, parameters)})`
    }
    if (locationName) {
      sql += ' ON $(locationName:name)'
      parameters.locationName = locationName
    }
    if (formatName) {
      sql += ' FILE FORMAT $(formatName:name)'
      parameters.formatName = formatName
    }
    if (formatType) {
      sql += ` TYPE ${formatType}`
    }
    if (commandOptions?.length) {
      sql += ` WITH (${this.formatOptions(commandOptions, parameters)})`
    }
    sql += ' LIMIT $(rowLimit) SKIP $(skipRows)'
    if (options?.preflight) {
      return { results: { sql: await instanceQueryService.formatQuery(sql, parameters) } }
    }
    return await instanceQueryService.sql(instanceId, database, this.withOptions(sql, options), { parameters, asyncTimeout: 30 * 1000 })
  }

  /**
   * Loads a table.
   *
   * @param database - database associated to this external table.
   * @param options - OPTIONAL options are a map of key / value entries, example for controlling GUC settings on the back-end:
   *        ex. set tags and hints {"GUC:ybd_query_tags": "testtag", "GUC:ybd_hint": "injectLatencySubmit=5000"}
   * @param tableName - tableName is the name of the table.
   * @param colSpecs - OPTIONAL columnSpecs is a list of key / value pairs listing each column name and column type,
   *        ex. [{"column1": "varchar(255)"}, {"column2": "varchar(255)"}, {"column3": "varchar(255)"}]
   * @param fileGlob - fileGlob is a list of file names or file name prefixes to assocate to the external table,
   *        ex. ['my-file.csv', 'test']
   *        would associate file 'my-file.csv' and any file starting with 'test'
   * @param locationName - locationName is the name of the location associated to this external table
   * @param formatName - formatName is the external format name associated to this table
   * @param formatType - OPTIONAL formatType is not used if  formatName is populated, otherwise it is required, ex. 'csv'
   * @param formatTypeOptions - OPTIONAL formatTypeOptions is not used if formatName is populated, otherwise it is a list of one or more format options,
   *        ex. [{"nullmarker": "<NULL>"}, {"emptymarker": "<EMPTY>"}]
   * @param commandOptions - OPTIONAL commandOptions are a list of name / value pairs and are used if additional configuration is
   *        required to access this external storage, ex. exclude certain files from user purview ["--exclude": "PROD"]
   * @returns {Promise<*>}
   */
  async load(
    instanceId: string,
    database: string,
    options: Record<string, string>,
    tableName: string,
    colSpecs: ColSpec[],
    fileGlobs: string[],
    locationName: string,
    formatName: string,
    formatType: string,
    formatTypeOptions: Option[],
    commandOptions: Option[]
  ): Promise<any> {
    const parameters: Record<string, any> = { tableName, locationName }
    let sql = 'LOAD TABLE $(tableName:alias)'
    if (colSpecs?.length) {
      sql += ` SOURCE FIELDS (${this.formatColSpecs(colSpecs, parameters)})`
    }
    if (fileGlobs?.length) {
      sql += ` FROM (${this.formatStringList(fileGlobs, parameters)})`
    }
    sql += ' EXTERNAL LOCATION $(locationName:name)'
    if (formatName) {
      sql += ' EXTERNAL FORMAT $(formatName:name)'
      parameters.formatName = formatName
    } else if (formatType) {
      sql += ` EXTERNAL FORMAT (TYPE ${formatType}`
      if (formatTypeOptions?.length) {
        sql += ` WITH (${this.formatOptions(formatTypeOptions, parameters)})`
      }
      sql += ')'
    }
    if (commandOptions?.length) {
      sql += ` WITH (${this.formatOptions(commandOptions, parameters)})`
    }
    if (options?.preflight) {
      return { results: { sql: await instanceQueryService.formatQuery(sql, parameters) } }
    }
    return this.convertRowSet(await instanceQueryService.sql(instanceId, database, this.withOptions(sql, options), { parameters, asyncTimeout: 30 * 1000, applicationName: 'ym' }))
  }

  async unload(
    instanceId: string,
    database: string,
    options: Record<string, string>,
    objectName: string,
    query: string,
    fileName: string,
    locationName: string,
    commandOptions: Option[],
    fileFormat: string,
    whereCondition: string
  ): Promise<any> {
    const parameters: Record<string, any> = { objectName, fileName, locationName }
    let sql = 'UNLOAD $(objectName:name)'
    if (query) {
      sql += ' $(query)'
      parameters.query = query
    }
    sql += ' TO $(fileName) ON $(locationName:name)'
    if (commandOptions?.length) {
      sql += ` WITH (${this.formatOptions(commandOptions, parameters)})`
    }
    if (fileFormat) {
      sql += ' FILE FORMAT $(fileFormat)'
      parameters.fileFormat = fileFormat
    }
    if (whereCondition) {
      sql += ' WHERE $(whereCondition)'
      parameters.whereCondition = whereCondition
    }
    if (options?.preflight) {
      return { results: { sql: await instanceQueryService.formatQuery(sql, parameters) } }
    }
    return this.convertRowSet(await instanceQueryService.sql(instanceId, database, this.withOptions(sql, options), { parameters, asyncTimeout: 30 * 1000, applicationName: 'ym' }))
  }

  convertRowSet(rowset: any) {

    // Convert the rowset to the dm api result.
    const warnings = (rowset.warnings || []).join('\n')
    const col = rowset.columns && rowset.columns[0].name
    const output = rowset.rows && rowset.rows.map(row => row[col]).join('\n')
    return {
      results: {
        sql_warning_message: warnings || null,
        sql_output: output,
        sql_warning_error_code: rowset.errorCode,
        sql_warning_sql_state: rowset.sqlState,
        yb_last_execid: rowset.execId
      }
    }
  }
}

const externalService = new ExternalService()
export default externalService
